
import { useEffect, useMemo, useRef, useState } from 'react';
import { FLAGS } from 'constants/classifier';
import './style.scss';

const FlagFilter = ({ value = 0, onChange, style, size }) => {
    const [search, setSearch] = useState('');
    const [open, setOpen] = useState(false);
    const [highlighted, setHighlighted] = useState(0);
    const menuRef = useRef(null);

    const options = useMemo(
        () => Object.entries(FLAGS).map(([label, bit]) => ({ label, bit })),
        [],
    );

    const normalize = (text) => text.toLowerCase().replace(/\s+/g, '_');

    const filtered = options.filter(
        ({ label, bit }) =>
            normalize(label).includes(normalize(search.trim())) &&
            !(value & bit),
    );

    useEffect(() => {
        setHighlighted(0);
    }, [search, value]);

    useEffect(() => {
        if (!open || !menuRef.current) return;
        const item = menuRef.current.children[highlighted];
        if (item) item.scrollIntoView({ block: 'nearest' });
    }, [open, highlighted]);

    const add = (bit) => {
        onChange(value | bit);
        setSearch('');
        setOpen(false);
    };

    const remove = (bit) => {
        onChange(value & ~bit);
    };

    return (
        <div className={size === 'small' ? 'flag-filter is-small' : 'flag-filter'} style={style}>
            <div className="flag-select">
                <input
                    value={search}
                    placeholder="Select flags..."
                    onFocus={() => setOpen(true)}
                    onChange={(e) => {
                        setSearch(e.target.value);
                        setOpen(true);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Escape') setOpen(false);
                        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                            e.preventDefault();
                            if (!open) {
                                setOpen(true);
                                return;
                            }
                            if (!filtered.length) return;
                            const step = e.key === 'ArrowDown' ? 1 : -1;
                            setHighlighted((i) => (Math.min(i, filtered.length - 1) + step + filtered.length) % filtered.length);
                        }
                        if (e.key === 'Enter' && open && filtered.length) {
                            e.preventDefault();
                            add(filtered[Math.min(highlighted, filtered.length - 1)].bit);
                        }
                    }}
                />

                <span className="arrow">▾</span>

                {open && (
                    <>
                        <div
                            className="backdrop"
                            onClick={() => setOpen(false)}
                        />
                        <div className="menu" ref={menuRef}>
                            {filtered.length ? (
                                filtered.map(({ label, bit }, index) => (
                                    <button
                                        type="button"
                                        key={bit}
                                        className={index === highlighted ? 'is-highlighted' : undefined}
                                        onMouseEnter={() => setHighlighted(index)}
                                        onClick={() => add(bit)}
                                    >
                                        {label}
                                    </button>
                                ))
                            ) : (
                                <div className="empty">
                                    No flags found
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>

            <div className="selected">
                {options
                    .filter(({ bit }) => value & bit)
                    .map(({ label, bit }) => (
                        <span className="chip" key={bit} style={{ color: FLAGS[label] >= 1 << 20 ? '#38b2ac' : '#e53e3e' }}>
                            {label}
                            <button
                                type="button"
                                aria-label={`Remove ${label}`}
                                onClick={() => remove(bit)}
                            >
                                ×
                            </button>
                        </span>
                    ))}
            </div>
        </div>
    );
};

export default FlagFilter;
